import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Recipe } from '@/types/recipe'
import type { BasketItem, WeekMenuItem } from '@/types/menu'
import { aiRecipeParser } from '@/services/ai-recipe-parser'
import {
  buildOwnCandidates,
  buildPoolCandidates,
  getCurrentWeekKey,
  interleaveMenuItems,
  pickWeekRotation,
  planMenuComposition,
  selectAnchorRecipes,
  toAnchorSummaries,
  toCandidateSummaries,
} from '@/services/menu-service'

interface MenuState {
  weekKey: string | null
  contextKey: string | null
  preferences: string
  items: WeekMenuItem[]
  usedAI: boolean
  /** True when dishes are still missing that only AI can supply. */
  awaitingAI: boolean
  generatedAt: string | null
  isGenerating: boolean
  error: string | null
  basket: BasketItem[]
}

interface MenuActions {
  generateMenu: (
    recipes: Recipe[],
    options: {
      contextKey: string
      preferences?: string
      useAI: boolean
      userId?: string
      householdId?: string
    }
  ) => Promise<void>
  setIdeaRecipe: (key: string, recipe: Partial<Recipe>) => void
  markIdeaSaved: (key: string, recipeId: string) => void
  addToBasket: (item: BasketItem) => void
  removeFromBasket: (key: string) => void
  setBasketServings: (key: string, servings: number) => void
  clearBasket: () => void
  clearError: () => void
}

type MenuStore = MenuState & MenuActions

export const useMenuStore = create<MenuStore>()(
  persist(
    (set, get) => ({
      weekKey: null,
      contextKey: null,
      preferences: '',
      items: [],
      usedAI: false,
      awaitingAI: false,
      generatedAt: null,
      isGenerating: false,
      error: null,
      basket: [],

      generateMenu: async (
        recipes,
        { contextKey, preferences = '', useAI, userId, householdId }
      ) => {
        if (get().isGenerating) {
          return
        }

        const weekKey = getCurrentWeekKey()
        const poolCandidates = buildPoolCandidates(recipes)
        const ownCandidates = buildOwnCandidates(recipes, userId, householdId)

        if (!poolCandidates.length && !ownCandidates.length) {
          set({
            weekKey,
            contextKey,
            preferences,
            items: [],
            usedAI: false,
            awaitingAI: false,
            generatedAt: new Date().toISOString(),
            error: null,
          })
          return
        }

        set({ isGenerating: true, error: null })

        const seed = `${weekKey}:${contextKey}`
        const { collectionCount, curatedCount, ideaCount } = planMenuComposition(
          ownCandidates.length,
          poolCandidates.length
        )

        const toCollectionItem = (recipe: Recipe, reason?: string): WeekMenuItem => ({
          kind: 'collection',
          key: `collection-${recipe.id}`,
          recipeId: recipe.id,
          reason,
        })

        const poolItems = pickWeekRotation(poolCandidates, curatedCount, `${seed}:pool`).map(
          (recipe) => toCollectionItem(recipe)
        )
        const ownItems = pickWeekRotation(ownCandidates, collectionCount, `${seed}:own`).map(
          (recipe) => toCollectionItem(recipe)
        )

        // A full pool means the week is complete without generating anything.
        if (ideaCount === 0) {
          set({
            weekKey,
            contextKey,
            preferences,
            items: interleaveMenuItems(ownItems, poolItems),
            usedAI: false,
            awaitingAI: false,
            generatedAt: new Date().toISOString(),
            isGenerating: false,
          })
          return
        }

        // Not enough reviewed dishes yet: top the week up from whatever is left
        // so the page is never thin, and mark it as still wanting AI dishes.
        const usedIds = new Set([...ownItems, ...poolItems].map((item) => item.key))
        const leftovers = [...poolCandidates, ...ownCandidates]
          .filter((recipe) => !usedIds.has(`collection-${recipe.id}`))
          .slice(0, ideaCount)
          .map((recipe) => toCollectionItem(recipe))

        const withoutAI: WeekMenuItem[] = interleaveMenuItems(
          [...ownItems, ...leftovers],
          poolItems
        )

        if (!useAI) {
          set({
            weekKey,
            contextKey,
            preferences,
            items: withoutAI,
            usedAI: false,
            awaitingAI: true,
            generatedAt: new Date().toISOString(),
            isGenerating: false,
          })
          return
        }

        const selectionSource = ownCandidates.length ? ownCandidates : poolCandidates
        const shortlist = pickWeekRotation(selectionSource, 60, seed)
        const result = await aiRecipeParser.curateWeekMenu(toCandidateSummaries(shortlist), {
          anchors: toAnchorSummaries(selectAnchorRecipes(recipes)),
          count: collectionCount,
          ideaCount,
          preferences: preferences || undefined,
        })

        if (!result.success) {
          set({
            weekKey,
            contextKey,
            preferences,
            items: withoutAI,
            usedAI: false,
            awaitingAI: true,
            generatedAt: new Date().toISOString(),
            isGenerating: false,
            error: result.error || 'Het weekmenu kon niet slim worden samengesteld.',
          })
          return
        }

        const ownRecipesById = new Map(ownCandidates.map((recipe) => [recipe.id, recipe]))
        const selectionItems: WeekMenuItem[] = (result.selection ?? [])
          .filter((entry) => ownRecipesById.has(entry.id))
          .map((entry) => ({
            kind: 'collection',
            key: `collection-${entry.id}`,
            recipeId: entry.id,
            reason: entry.reason || undefined,
          }))

        const ideaItems: WeekMenuItem[] = (result.ideas ?? []).map((idea) => ({
          kind: 'idea',
          key: `idea-${weekKey}-${idea.id}`,
          idea,
        }))

        set({
          weekKey,
          contextKey,
          preferences,
          items: interleaveMenuItems([...selectionItems, ...poolItems], ideaItems),
          usedAI: true,
          awaitingAI: ideaItems.length < ideaCount,
          generatedAt: new Date().toISOString(),
          isGenerating: false,
        })
      },

      setIdeaRecipe: (key, recipe) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.kind === 'idea' && item.key === key ? { ...item, recipe } : item
          ),
        })),

      markIdeaSaved: (key, recipeId) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.kind === 'idea' && item.key === key ? { ...item, savedRecipeId: recipeId } : item
          ),
          basket: state.basket.map((item) =>
            item.key === key ? { ...item, recipeId, source: 'collection' } : item
          ),
        })),

      addToBasket: (item) =>
        set((state) => ({
          basket: state.basket.some((existing) => existing.key === item.key)
            ? state.basket
            : [...state.basket, item],
        })),

      removeFromBasket: (key) =>
        set((state) => ({
          basket: state.basket.filter((item) => item.key !== key),
        })),

      setBasketServings: (key, servings) =>
        set((state) => ({
          basket: state.basket.map((item) =>
            item.key === key ? { ...item, servings: Math.max(1, Math.min(servings, 20)) } : item
          ),
        })),

      clearBasket: () => set({ basket: [] }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'menu-storage',
      partialize: (state) => ({
        weekKey: state.weekKey,
        contextKey: state.contextKey,
        preferences: state.preferences,
        items: state.items,
        usedAI: state.usedAI,
        awaitingAI: state.awaitingAI,
        generatedAt: state.generatedAt,
        basket: state.basket,
      }),
    }
  )
)
